import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
// eslint-disable-next-line import/no-unresolved
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '~/components/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface TFormValues {
  topic: string;
  question: string;
}

const options = [
  {
    label: 'Financieel',
    value: 'financieel',
  },
];

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  // Cancel the event
  event.preventDefault();
  // Chrome requires returnValue to be set
  // event.returnValue = '';
  // Show the confirmation alert
  return "The page that you're looking for used information that you entered. Returning to that page might cause any action that you took to be repeated. Do you want to continue?";
};

const QuestionForm: React.FC<{
  setActiveStepId: React.Dispatch<React.SetStateAction<number>>;
  handleOnSubmit: (data: TFormValues) => Promise<void>;
}> = ({ setActiveStepId, handleOnSubmit }) => {
  const { register, handleSubmit, formState } = useForm<TFormValues>({
    mode: 'onSubmit',
  });

  useEffect(() => {
    // Add event listener for beforeunload when component mounts
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Remove event listener when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const { errors, isSubmitting } = formState;

  const onSubmit = async ({ topic, question }: TFormValues): Promise<void> => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    await handleOnSubmit({ topic, question });
  };

  return (
    <form
      className="space-y-[24px] w-full"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <div className="space-y-[12px] w-full">
        <label
          htmlFor="topic"
          className="text-blue-900 font-bold font-avantgarde text-[14px] leading-[18.2px]"
        >
          Over welk onderwerp wilt u een vraag stellen{' '}
          <span className="text-tomato-700">*</span>
        </label>
        <div>
          <select
            {...register('topic')}
            id="topic"
            className="bg-white text-black outline-none focus:bg-gray-50 duration-200  border border-stone-200 px-[7px] py-[10px] rounded-[4px] w-full md:w-[40%]"
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {errors.topic && <p className="text-tomato-700">{errors.topic.message}</p>}
      </div>
      <div className="space-y-[12px] w-full">
        <label
          htmlFor="question"
          className="text-blue-900 font-bold font-avantgarde text-[14px] leading-[18.2px]"
        >
          {' '}
          Stel uw vraag <span className="text-tomato-700">*</span>
        </label>
        <textarea
          id="question"
          placeholder="Vraagstelling"
          className="w-full border border-stone-200 px-[7px] py-[10px] rounded-[4px] outline-none h-[120px] placeholder-stone-100"
          {...register('question', {
            required: {
              value: true,
              message: 'Dit veld is verplicht',
            },
            minLength: {
              value: 10,
              message: 'Minimaal 10 en maximaal 1000 karakters',
            },
            maxLength: {
              value: 1000,
              message: 'Minimaal 10 en maximaal 1000 karakters',
            },
          })}
        />
        {errors.question && (
          <p className="text-tomato-700">{errors.question.message}</p>
        )}
      </div>
      <div className="w-full border border-stone-200 bg-blue-50 px-[12px] py-[16px] rounded-[4px]">
        <p className="text-stone-800 font-normal font-body text-[16px] leading-[26.4px]">
          <span className="font-bold">Let op:</span> De vraag en de gegeven
          antwoorden worden anoniem gemaakt en getoond op Advieskeuze.nl
        </p>
      </div>
      <div className="flex flex-col md:flex-row gap-[24px] pt-[24px]">
        <Button
          type="button"
          onClick={() => setActiveStepId(1)}
          variant="default"
        >
          <FontAwesomeIcon icon={faChevronLeft as IconProp} />
          Terug
        </Button>
        
        <Button
          data-testId='submit-question'
          type="submit"
          variant={isSubmitting ? 'default' : 'primary'}
        >
          Verzenden
          <FontAwesomeIcon icon={faChevronRight as IconProp} />
        </Button>
      </div>
    </form>
  );
};

// eslint-disable-next-line import/no-default-export
export default QuestionForm;
